import PropTypes from 'prop-types';
import styled, { css, useTheme } from 'styled-components';

const NO_FLAG_NAME = 'NOFLAG';

const importAll = (requireContext: __WebpackModuleApi.RequireContext) => requireContext.keys().map(requireContext);
const svgIconFiles = require.context('!@svgr/webpack!~/assets/icons', false, /\.svg$/);
const svgFlagFiles = require.context('!@svgr/webpack!~/assets/flags', false, /\.svg$/);

importAll(svgIconFiles);
importAll(svgFlagFiles);

const inlineCSS = css`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const notInlineCSS = css`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

interface IconContainerProps {
  width?: string | number;
  height?: string | number;
  isHovered?: boolean;
  color?: string;
  hoveredColor?: string;
  isHoverable?: boolean;
  inline?: boolean;
}

const IconContainer = styled.div<IconContainerProps>`
  & svg {
    height: ${props => (props.height ? `${props.height}px` : '100%')};
    width: ${props => (props.width ? `${props.width}px` : '100%')};
    & path {
      fill: ${props => (props.isHovered ? props.hoveredColor : props.color)};
    }
  }
  ${props => (props.inline ? inlineCSS : notInlineCSS)}
  ${props =>
    props.isHoverable &&
    css<IconContainerProps>`
      &:hover {
        & svg {
          & path {
            fill: ${props => props.hoveredColor};
          }
        }
      }
    `}
`;

export interface IconProps {
  name: string;
  width?: string | number;
  height?: string | number;
  isHovered?: boolean;
  isHoverable?: boolean;
  color?: string;
  hoveredColor?: string;
  inline?: boolean;
  className?: string;
  isFlag?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

const Icon = ({
  name,
  width,
  height,
  isHovered,
  isHoverable,
  color,
  hoveredColor,
  inline,
  className,
  isFlag,
  onClick,
}: IconProps) => {
  const theme = useTheme();

  const files = isFlag ? svgFlagFiles : svgIconFiles;
  let fileKey = `./${name}.svg`;

  if (!files.keys().includes(fileKey)) {
    console.warn(`Icon ${name} not found in ${isFlag ? 'flags' : 'icons'}`);
    if (isFlag) {
      fileKey = `./${NO_FLAG_NAME}.svg`;
      if (!files.keys().includes(fileKey)) {
        console.warn(`Icon ${NO_FLAG_NAME} not found in flags`);
        return null;
      }
    } else {
      return null;
    }
  }

  const svgFile = files(fileKey).default();

  if (!name) return;
  return (
    <IconContainer
      width={width}
      height={height}
      isHovered={isHovered}
      color={color !== undefined ? color : theme?.color.neutral[12]}
      hoveredColor={hoveredColor !== undefined ? hoveredColor : theme?.color.neutralAlpha[12]}
      isHoverable={isHoverable}
      inline={inline}
      className={className}
      onClick={e => onClick?.(e)}
    >
      {svgFile}
    </IconContainer>
  );
};

Icon.propTypes = {
  name: PropTypes.string.isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isHovered: PropTypes.bool,
  color: PropTypes.string,
  hoveredColor: PropTypes.string,
  isHoverable: PropTypes.bool,
  inline: PropTypes.bool,
  className: PropTypes.string,
  isFlag: PropTypes.bool,
  onClick: PropTypes.func,
};

export default Icon;
